import { useEffect,useState } from 'react';
import { useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    openDrawer,
    setCurrentDrawerId,
    setCurrentEntity,
    setCurrentOperation
} from 'common/components/EditDrawer/editDrawerSlice';
import { UserProfileEntityContainer } from 'common/components/UserProfileEntityContainer/UserProfileEntityContainer';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchForeignSystems } from 'common/services/api/ForeignSystems/ForeignSystems.thunk';
import { fetchUserProfileForeignSystems } from 'common/services/api/UserProfileForeignSystems/UserProfileForeignSystems.thunk';
import {
    DrawerEntitiesNames,
    Operations
} from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { UserForeignSystemList } from './components/UserForeignSystemsList';
import {
    selectUserForeignKeysList,
    setUserProfileForeignKeys } from './userProfileForeignSystemsSlice';

const CONTAINER_INLINE_STYLES = {
    backgroundColor: 'background.paper',
    borderRadius: '4px',
    border: 1,
    borderColor: 'info.dark',
    flexBasis: '33.33%',
    marginRight: '24px',
    height: 'max-content'
};

export function UserProfileForeignSystems() {
    const [processing, setProcessing] = useState<boolean>(false);
    const params = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const userForeignKeysList = useAppSelector(selectUserForeignKeysList);
    const shouldDisableLinkButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    useEffect(() => {
        const fetchUserForeignKeysParams = {
            userId: params?.userId ?? ''
        };
        setProcessing(true);
        dispatch(fetchUserProfileForeignSystems(fetchUserForeignKeysParams))
            .unwrap()
            .then((result) => {
                dispatch(setUserProfileForeignKeys(result));
            })
            .catch((error) => {
                dispatch(setUserProfileForeignKeys([]));
                handleListFetchError(extractStatusCode(error.message), 'Foreign Keys', enqueueSnackbar);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    useEffect(() => {
        dispatch(fetchForeignSystems());
    }, []);

    const handleCreateButtonClick = () => {
        dispatch(setCurrentOperation(Operations.Link));
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserForeignSystems));
        dispatch(setCurrentDrawerId(DrawerIDs.UserProfileForeignSystems));
        dispatch(openDrawer());
    };

    const userForeignSystemListToRender = (processing ? <CircularProgress color='primary'/> :
        <UserForeignSystemList data={userForeignKeysList} />);

    return (
        <UserProfileEntityContainer
            title='Foreign Systems'
            isCreateButtonDisabled={shouldDisableLinkButton}
            shouldShowCreateButton={true}
            containerInlineStyles={CONTAINER_INLINE_STYLES}
            createButtonHandler={handleCreateButtonClick}
        >
            {userForeignSystemListToRender}
        </UserProfileEntityContainer>
    );
}
